<template>
  <div
    class="relative overflow-hidden transition-colors duration-100 ease-out rounded"
    :class="{
      'bg-green-200 bg-opacity-50': (!!existingFile || !!fileOnHold) && !isMouseOver,
      'bg-gray-300': isDraggingOver || !isMouseOver,
      'bg-orange-mia': !isDraggingOver && isMouseOver,
      'border border-gray-400': !error && !isDraggingOver,
      'border border-orange-mia': isDraggingOver,
      'border border-red-600': error && !isDraggingOver
    }"
    @dragenter="onDragEnter"
    @dragleave="onDragLeave"
    @dragover="onDragOver"
    @drop="onDrop"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="relative z-10 flex flex-col h-40 p-4">
      <template v-if="!isUploading">
        <div
          v-if="existingFile || fileOnHold"
          class="flex justify-center flex-1"
        >
          <img
            v-if="previewImage"
            :src="previewImage"
            class="h-24"
          >
          <mia-streamline-icon
            v-else
            class="inline-block w-12 h-12 mt-4 fill-current"
            src="office-file-pdf"
          />
        </div>
        <div
          v-else
          class="flex-1 pt-6 text-center"
          :class="{
            'text-gray-700': isDraggingOver || !isMouseOver,
            'text-white': !isDraggingOver && isMouseOver
          }"
        >
          <p v-if="!canUpload">
            {{ category.hint || 'Dieses Dokument kann nur vom Kunden hochgeladen werden' }}
          </p>
          <p v-else>
            Klicken oder Dokument zum Hochladen hier her ziehen
          </p>
        </div>
        <div
          class="flex items-center justify-center font-semibold tracking-wide text-center"
          :class="{
            'text-gray-800': isDraggingOver || !isMouseOver,
            'text-gray-100': !isDraggingOver && isMouseOver
          }"
        >
          <span>{{ category.label }}</span><mia-streamline-icon
            v-if="existingFile || fileOnHold"
            class="w-4 h-4 ml-2 text-green-600 fill-current"
            src="check-circle-1"
          />
        </div>
      </template>
      <div
        v-else
        class="flex items-center justify-center w-full h-full"
      >
        <mia-spinner class="w-6 h-6 text-orange-mia" />
      </div>

      <!-- eslint-disable vue/no-v-html -->
      <small
        v-if="error"
        class="absolute block leading-3 text-red-600 pointer-events-none top-0 mt-4"
        v-html="error"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <input
      v-if="canUpload && !isUploading"
      ref="fileInputRef"
      type="file"
      class="absolute top-0 left-0 z-20 w-full h-full opacity-0 cursor-pointer focus:outline-none"
      accept=".pdf"
      @change="onFileSelected"
    >
    <div class="absolute left-0 top-0 z-30 p-1">
      <div class="bg-gray-700 rounded shadow-lg flex divide-x divide-gray-600">
        <template v-if="fileOnHold">
          <button
            v-tooltip="'Datei löschen'"
            type="button"
            class="py-2 px-3 text-white first:rounded-l last:rounded-r hover:bg-red-600 focus:outline-none"
            title="Datei löschen"
            @click="deleteFileOnHold"
          >
            <mia-streamline-icon
              class="w-5 h-5 fill-current"
              src="bin-1"
            />
          </button>
        </template>
        <template v-else-if="existingFile && ((car.item_access_locked === 'N' || isAllowed('handle_car_customer_access')))">
          <button
            v-tooltip="'Datei herunterladen'"
            type="button"
            class="py-2 px-3 text-white first:rounded-l last:rounded-r hover:bg-gray-600 focus:outline-none"
            title="Datei herunterladen"
            @click="downloadExistingFile"
          >
            <mia-streamline-icon
              class="w-5 h-5 fill-current"
              src="download-button"
            />
          </button>
          <button
            v-tooltip="'Datei löschen'"
            type="button"
            class="py-2 px-3 text-white first:rounded-l last:rounded-r hover:bg-red-600 focus:outline-none"
            title="Datei löschen"
            @click="deleteExistingFile"
          >
            <mia-streamline-icon
              class="w-5 h-5 fill-current"
              src="bin-1"
            />
          </button>
        </template>
      </div>
    </div>

    <div
      v-if="isOrderConfirmation && hasOrderConfirmationBeenReceived"
      v-tooltip="orderConfirmationTooltip"
      class="absolute top-0 right-0 z-30 p-2 cursor-help"
    >
      <mia-streamline-icon
        class="w-5 h-5 text-blue-600 fill-current"
        src="check-circle-1"
      />
    </div>

    <mia-car-file-customer-invoice-modal
      v-if="showCustomerInvoiceModal"
      :show="showCustomerInvoiceModal"
      :is-uploading-file="isUploading"
      @cancel="onCustomerInvoiceUploadCancelled"
      @closed="showCustomerInvoiceModal = false"
      @saved="onCustomerInvoiceDataSaved"
    />
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import Spinner from '@/components/ui/loader/Spinner.vue'
import CarFileCustomerInvoiceModal from '@/components/cars/CarFileCustomerInvoiceModal.vue'
import { trashFile } from '@/api/cars'

export default {
  name: 'CarFileCustomerDocument',

  components: {
    'mia-car-file-customer-invoice-modal': CarFileCustomerInvoiceModal,
    'mia-spinner': Spinner
  },

  props: {
    category: {
      type: Object,
      required: true
    },

    isUploading: {
      type: Boolean,
      default: false
    },

    existingFile: {
      type: Object,
      default: null,
      validate: () => true
    },

    error: {
      type: String,
      default: null,
      validate: () => true
    },

    temporary: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    isDraggingOver: false,
    isMouseOver: false,
    showCustomerInvoiceModal: false,
    fileOnHold: null
  }),

  computed: {
    previewImage() {
      if (!this.existingFile || !this.existingFile.previewImage) {
        return null
      }

      if (/^https/.test(this.existingFile.previewImage)) {
        return this.existingFile.previewImage
      }

      return `${process.env.VUE_APP_PROVIDER}/car/pdfPreview/${this.existingFile.previewImage}`
    },

    downloadUrl() {
      return `${this.existingFile.id}`.startsWith('temp-') ? `${window.baseUrl}/car/getTempfile/${this.key}` : `${window.baseUrl}/car/getFile/${this.existingFile.id}`
    },

    canUpload() {
      return this.category.allowed_sources !== false && (this.category.allowed_sources.includes('dealer') || this.category.allowed_sources === '*')
    },

    car() {
      return this.$store.state.car.car
    },

    isOrderConfirmation() {
      return this.category.id === 'order_confirmation'
    },

    hasOrderConfirmationBeenReceived() {
      return !!this.car.item_order_confirmation_received_at
    },

    orderConfirmationTooltip() {
      const date = DateTime.fromSQL(this.car.item_order_confirmation_received_at)

      return `Vom Kunden am ${date.toFormat('dd.MM.yyyy')} um ${date.toFormat('HH:mm')} Uhr geöffnet`
    },

    isCustomerInvoice() {
      return this.category.id === 'customer_invoice'
    },

    shouldHoldFile() {
      return this.isCustomerInvoice || this.temporary
    }
  },

  watch: {
    isUploading() {
      this.$nextTick(() => {
        this.fileOnHold = null
        this.showCustomerInvoiceModal = false
      })
    }
  },

  methods: {
    downloadExistingFile() {
      if (this.car.item_access_locked === 'N' || this.isAllowed('handle_car_customer_access')) {
        window.open(this.downloadUrl)
      }
    },

    async deleteExistingFile() {
      const [error, data] = await trashFile(this.existingFile)

      if (error || !data.success) {
        if (data.message) {
          window.eventBus.$emit('mia-error', {
            error: [data.message.replace(/&bull;/g, '•')]
          })
        } else {
          window.eventBus.$emit('mia-error', {
            error: ['Datei konnte nicht gelöscht werden.']
          })
        }

        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Datei erfolgreich gelöscht']
      })
      this.$emit('deleted', this.existingFile)
    },

    deleteFileOnHold() {
      this.fileOnHold = null
      this.$emit('deleted')
    },

    onDragEnter(event) {
      if (!this.canUpload) {
        return
      }

      event.preventDefault()
      this.isDraggingOver = true
    },

    onDragLeave() {
      if (!this.canUpload) {
        return
      }

      this.isDraggingOver = false
    },

    onDragOver(event) {
      if (!this.canUpload) {
        return
      }

      event.preventDefault()
      this.isDraggingOver = true
    },

    onDrop(event) {
      if (!this.canUpload) {
        return
      }

      event.preventDefault()
      this.isDraggingOver = false

      if (this.isCustomerInvoice) {
        this.showCustomerInvoiceModal = true
      }

      if (this.shouldHoldFile) {
        this.fileOnHold = event.dataTransfer.items[0].getAsFile()
        return
      }

      this.emitUploadEvent(event.dataTransfer.items[0].getAsFile())
    },

    onFileSelected(event) {
      event.preventDefault()

      if (this.isCustomerInvoice) {
        this.showCustomerInvoiceModal = true
      }

      if (this.shouldHoldFile) {
        // eslint-disable-next-line prefer-destructuring
        this.fileOnHold = event.target.files[0]
        return
      }

      this.emitUploadEvent(event.target.files[0])
    },

    emitUploadEvent(file) {
      this.$emit('upload', {
        file,
        category: this.category
      })
    },

    onMouseEnter() {
      if (!this.canUpload) {
        return
      }

      this.isMouseOver = true
    },

    onMouseLeave() {
      if (!this.canUpload) {
        return
      }

      this.isMouseOver = false
    },

    onCustomerInvoiceUploadCancelled() {
      this.fileOnHold = null
      this.showCustomerInvoiceModal = false
      this.$refs.fileInputRef.value = ''
    },

    onCustomerInvoiceDataSaved() {
      this.emitUploadEvent(this.fileOnHold)
    }
  }
}
</script>
